import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import MotionSection from '../components/ui/MotionSection';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit3, FiTrash2, FiDownload, FiFilter, FiSearch, FiCalendar, FiMail } = FiIcons;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { projects, emails } = useData();
  const [activeTab, setActiveTab] = useState('projects');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');

  if (!user) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Please sign in to access your dashboard</h2>
          <Link to="/login" className="btn-primary px-6 py-3 rounded-lg text-white font-medium">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  const filteredEmails = emails.filter(email => {
    const matchesSearch = email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.body.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterBy === 'all' || email.persona === filterBy || email.tone === filterBy;
    return matchesSearch && matchesFilter;
  });

  const tabs = [
    { id: 'projects', name: 'Projects', count: projects.length },
    { id: 'emails', name: 'Saved Emails', count: emails.length },
    { id: 'history', name: 'History', count: 12 },
    { id: 'settings', name: 'Settings', count: null }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <MotionSection className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl font-bold mb-2">Welcome back, {user.name}!</h1>
              <p className="text-light/60">Manage your projects, emails, and settings.</p>
            </div>
            <Link
              to="/app"
              className="mt-4 md:mt-0 btn-primary px-6 py-3 rounded-lg text-white font-medium flex items-center space-x-2"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Generate New Email</span>
            </Link>
          </div>
        </MotionSection>

        {/* Stats Cards */}
        <MotionSection className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="gradient-border rounded-xl p-6">
            <div className="gradient-border-inner p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-light/60 text-sm">Total Projects</p>
                  <p className="text-2xl font-bold">{projects.length}</p>
                </div>
                <SafeIcon icon={FiEdit3} className="w-8 h-8 text-primary" />
              </div>
            </div>
          </div>

          <div className="gradient-border rounded-xl p-6">
            <div className="gradient-border-inner p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-light/60 text-sm">Saved Emails</p>
                  <p className="text-2xl font-bold">{emails.length}</p>
                </div>
                <SafeIcon icon={FiMail} className="w-8 h-8 text-primary" />
              </div>
            </div>
          </div>

          <div className="gradient-border rounded-xl p-6">
            <div className="gradient-border-inner p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-light/60 text-sm">This Month</p>
                  <p className="text-2xl font-bold">47</p>
                </div>
                <SafeIcon icon={FiCalendar} className="w-8 h-8 text-primary" />
              </div>
            </div>
          </div>

          <div className="gradient-border rounded-xl p-6">
            <div className="gradient-border-inner p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-light/60 text-sm">Exports</p>
                  <p className="text-2xl font-bold">12</p>
                </div>
                <SafeIcon icon={FiDownload} className="w-8 h-8 text-primary" />
              </div>
            </div>
          </div>
        </MotionSection>

        {/* Tabs */}
        <MotionSection className="mb-8">
          <div className="border-b border-primary/20">
            <nav className="flex space-x-8">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-light/60 hover:text-light hover:border-light/30'
                  }`}
                >
                  {tab.name}
                  {tab.count !== null && (
                    <span className="ml-2 bg-primary/20 text-primary px-2 py-1 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </MotionSection>

        {/* Content */}
        <MotionSection>
          {activeTab === 'projects' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Your Projects</h2>
                <button className="btn-primary px-4 py-2 rounded-lg text-white font-medium flex items-center space-x-2">
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  <span>New Project</span>
                </button>
              </div>

              {projects.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-light/60 mb-4">No projects yet.</p>
                  <Link
                    to="/app"
                    className="btn-primary px-6 py-3 rounded-lg text-white font-medium"
                  >
                    Create Your First Project
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map(project => (
                    <div key={project.id} className="gradient-border rounded-xl p-6">
                      <div className="gradient-border-inner p-6">
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="font-semibold">{project.name}</h3>
                          <button className="text-light/60 hover:text-light">
                            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-light/60 text-sm mb-4">{project.notes}</p>
                        <div className="flex justify-between items-center text-sm text-light/60">
                          <span>{new Date(project.createdAt).toLocaleDateString()}</span>
                          <span>0 emails</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'emails' && (
            <div>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light/40" />
                    <input
                      type="text"
                      placeholder="Search emails..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
                <select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                  className="px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                >
                  <option value="all">All Emails</option>
                  <option value="Founder">Founder</option>
                  <option value="Marketing Lead">Marketing Lead</option>
                  <option value="SMB Owner">SMB Owner</option>
                  <option value="Friendly">Friendly Tone</option>
                  <option value="Formal">Formal Tone</option>
                </select>
                <button className="px-4 py-3 border border-primary/20 rounded-lg hover:border-primary transition-colors flex items-center space-x-2">
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  <span>Export All</span>
                </button>
              </div>

              {filteredEmails.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-light/60 mb-4">
                    {emails.length === 0 ? 'No saved emails yet.' : 'No emails match your search.'}
                  </p>
                  {emails.length === 0 && (
                    <Link
                      to="/app"
                      className="btn-primary px-6 py-3 rounded-lg text-white font-medium"
                    >
                      Generate Your First Email
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredEmails.map(email => (
                    <div key={email.id} className="gradient-border rounded-xl p-6">
                      <div className="gradient-border-inner p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="font-semibold mb-2">{email.subject}</h3>
                            <div className="flex flex-wrap gap-2 mb-3">
                              <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs">
                                {email.persona}
                              </span>
                              <span className="px-2 py-1 bg-secondary/20 text-secondary rounded text-xs">
                                {email.tone}
                              </span>
                              <span className="px-2 py-1 bg-light/20 text-light rounded text-xs">
                                {email.framework}
                              </span>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button className="p-2 hover:bg-primary/10 rounded-lg transition-colors">
                              <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                            </button>
                            <button className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-red-400">
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="bg-dark/50 p-4 rounded-lg mb-4">
                          <p className="text-sm line-clamp-3">{email.body}</p>
                        </div>
                        
                        <div className="flex justify-between items-center text-sm text-light/60">
                          <span>{new Date(email.createdAt).toLocaleDateString()}</span>
                          <button className="text-primary hover:underline">
                            View Full Email
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="text-center py-12">
              <p className="text-light/60 mb-4">Generation history coming soon!</p>
              <p className="text-sm text-light/40">
                Track your email generation history and re-run previous prompts.
              </p>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="max-w-2xl">
              <h2 className="text-xl font-semibold mb-6">Account Settings</h2>
              
              <div className="space-y-6">
                <div className="gradient-border rounded-xl p-6">
                  <div className="gradient-border-inner p-6">
                    <h3 className="font-semibold mb-4">Profile Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Name</label>
                        <input
                          type="text"
                          value={user.name}
                          className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Email</label>
                        <input
                          type="email"
                          value={user.email}
                          className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="gradient-border rounded-xl p-6">
                  <div className="gradient-border-inner p-6">
                    <h3 className="font-semibold mb-4">Preferences</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Default Tone</label>
                        <select className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary">
                          <option>Friendly</option>
                          <option>Formal</option>
                          <option>Bold</option>
                          <option>Conversational</option>
                          <option>Concise</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Signature</label>
                        <textarea
                          placeholder="Your email signature..."
                          className="w-full px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary resize-none h-20"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <button className="btn-primary px-6 py-3 rounded-lg text-white font-medium">
                    Save Changes
                  </button>
                  <button
                    onClick={logout}
                    className="px-6 py-3 border border-red-500/20 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          )}
        </MotionSection>
      </div>
    </motion.div>
  );
};

export default Dashboard;